'use client';

import { useState } from 'react';
import { Dialog, DialogPanel } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export default function BreachDetailsDrawer({ open, onClose, breach }) {
  // Format field labels for display
  const formatLabel = (key) => {
    // Handle special cases
    if (key === 'PwnCount') return 'Accounts Affected';
    if (key === 'DataClasses') return 'Data Compromised';
    if (key === 'IsVerified') return 'Verified Breach';
    if (key === 'IsFabricated') return 'Fabricated Breach';
    if (key === 'IsSensitive') return 'Sensitive Breach';
    if (key === 'IsRetired') return 'Retired Breach';
    if (key === 'IsSpamList') return 'Spam List';
    if (key === 'IsMalware') return 'Malware';
    if (key === 'IsStealerLog') return 'Stealer Log';
    if (key === 'IsSubscriptionFree') return 'Free Subscription';
    
    // For other keys, convert camelCase to Title Case with spaces
    return key.replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase());
  };

  // Format field values for display
  const formatValue = (key, value) => {
    if (key === 'PwnCount' && typeof value === 'number') {
      return value.toLocaleString();
    }
    
    if (key === 'DataClasses' && Array.isArray(value)) {
      return value.join(', ');
    }
    
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    return value;
  };

  // Create a safe HTML rendering function for Description
  const createMarkup = (htmlContent) => {
    return { __html: htmlContent };
  };

  // Fields to display in order
  const displayFields = [
    { key: 'Description', label: 'What happened?', isLarge: true, isHtml: true },
    { key: 'BreachDate', label: 'Breach Date' },
    { key: 'Domain', label: 'Domain' },
    { key: 'PwnCount', label: 'Accounts Affected' },
    { key: 'DataClasses', label: 'Data Compromised' },
    { key: 'AddedDate', label: 'Added Date' },
    { key: 'ModifiedDate', label: 'Modified Date' },
    { key: 'IsVerified', label: 'Verified Breach' },
    { key: 'IsFabricated', label: 'Fabricated Breach' },
    { key: 'IsSensitive', label: 'Sensitive Breach' },
    { key: 'IsRetired', label: 'Retired Breach' },
    { key: 'IsSpamList', label: 'Spam List' },
    { key: 'IsMalware', label: 'Malware' },
    { key: 'IsStealerLog', label: 'Stealer Log' },
    { key: 'IsSubscriptionFree', label: 'Free Subscription' },
  ];

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-gray-900/60" />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
            <DialogPanel
              transition
              className="pointer-events-auto w-screen max-w-md transform transition duration-500 ease-in-out data-closed:translate-x-full sm:duration-700"
            >
              <div className="flex h-full flex-col overflow-y-scroll bg-gray-900 shadow-xl">
                <div className="px-4 py-6 sm:px-6 border-b border-gray-700/50">
                  <div className="flex items-start justify-between">
                    <h2 id="slide-over-heading" className="text-xl font-bold text-white">
                      {breach.Title || 'Breach Details'}
                    </h2>
                    <div className="ml-3 flex h-7 items-center">
                      <button
                        type="button"
                        onClick={onClose}
                        className="relative rounded-md bg-gray-900 text-gray-400 hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <span className="absolute -inset-2.5" />
                        <span className="sr-only">Close panel</span>
                        <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Main content */}
                <div className="divide-y divide-gray-700/50">
                  <dl className="space-y-8 px-4 py-6 sm:space-y-6 sm:px-6">
                    {displayFields.map(({ key, label, isLarge, isHtml }) => {
                      // Skip fields that don't exist in the breach object
                      if (!(key in breach)) return null;
                      
                      const value = formatValue(key, breach[key]);
                      
                      return (
                        <div key={key} className="sm:flex">
                          <dt className="text-sm font-medium text-gray-400 sm:w-40 sm:flex-shrink-0 sm:pr-4">{label}</dt>
                          <dd className={`mt-1 text-sm text-gray-300 sm:mt-0 sm:ml-4 ${isLarge ? 'whitespace-normal' : ''}`}>
                            {isHtml ? (
                              <div dangerouslySetInnerHTML={createMarkup(value)} />
                            ) : isLarge ? (
                              <p className="whitespace-pre-line">{value}</p>
                            ) : (
                              value
                            )}
                          </dd>
                        </div>
                      );
                    })}
                    
                    {/* Logo display if available */}
                    {breach.LogoPath && (
                      <div className="sm:flex">
                        <dt className="text-sm font-medium text-gray-400 sm:w-40 sm:flex-shrink-0 sm:pr-4">Logo</dt>
                        <dd className="mt-1 text-sm text-gray-300 sm:mt-0 sm:ml-4">
                          <img 
                            src={breach.LogoPath} 
                            alt={`${breach.Title} logo`} 
                            className="h-16 w-auto rounded bg-white p-1"
                          />
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
} 